import React, { useEffect, useState, useCallback, useRef } from "react";
import { formatCurrency } from "../config/market";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { BounceLoader } from "react-spinners";
import useCatalogUpdates from "../hooks/useCatalogUpdates";

import Layout from "../components/Global/Layout";
import CategoryStrip from "../components/shop/CategoryStrip";
import FilterRail from "../components/shop/FilterRail";
import ProductCard from "../components/shop/ProductCard";
import CrawlLink from "../components/CrawlLink";
import { optimizedImage } from "../utils/cloudinaryImage";
import {
  fetchCatalog,
  fetchPromoListings,
  removeCatalogItem,
  removeCatalogVendorItems,
} from "../redux/listingRedux";
import {
  fetchCategories,
  selectActiveCategories,
  filterCategory,
} from "../redux/categoriesRedux";

// ── Layout ──
const Page = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 12px 14px 60px;
  overflow-x: hidden;
`;
const StripWrap = styled.div`
  margin: 6px 0 14px;
`;
const Shell = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 20px;
  /* Grid items default to min-width:auto, which lets wide content (cards,
     deals row) push the column past the viewport. Allow them to shrink. */
  & > * { min-width: 0; }
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const RailCol = styled.div`
  @media (max-width: 900px) { display: ${(p) => (p.$open ? "block" : "none")}; }
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;
const Count = styled.div`
  font-size: 14px;
  color: #6b7a80;
  b { color: #1f2a2e; }
`;
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e4e8ea;
  border-radius: 9px;
  padding: 0 10px;
  height: 38px;
  flex: 1;
  min-width: 180px;
  max-width: 360px;
  input { border: 0; outline: none; padding: 0 8px; font-size: 13px; flex: 1; background: transparent; }
`;
const SortWrap = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  label { font-size: 12px; color: #6b7a80; }
  select {
    border: 1px solid #e4e8ea; background: #fff; border-radius: 9px;
    padding: 9px 12px; font-size: 13px; font-weight: 600; color: #33454b; cursor: pointer; outline: none;
  }
`;
const FiltersBtn = styled.button`
  display: none;
  @media (max-width: 900px) { display: inline-flex; }
  align-items: center; gap: 6px;
  border: 1px solid #03660c; background: #fff; color: #03660c;
  border-radius: 9px; padding: 9px 12px; font-size: 13px; font-weight: 700; cursor: pointer;
`;
const Chips = styled.div`
  display: flex; gap: 7px; flex-wrap: wrap; margin: 10px 0 14px; min-height: 4px;
`;
const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  background: #eaf4e2; color: #2f6d12; border: 1px solid #cfe6bd;
  font-size: 12px; font-weight: 600; padding: 5px 10px; border-radius: 999px;
  .x { cursor: pointer; font-weight: 800; opacity: 0.7; }
`;
const ClearAll = styled(Chip)`
  background: #fbe9ef; color: #d63b6f; border-color: #f4c8d8; cursor: pointer;
`;
const DealsBox = styled.div`
  background: #fff; border: 1px solid #ffe0cf; border-radius: 12px;
  padding: 14px 16px; margin-bottom: 18px;
  box-shadow: 0 1px 4px rgba(20, 40, 45, 0.06);
  h3 { margin: 0 0 12px; font-size: 15px; color: #b5531f; display: flex; align-items: center; gap: 8px; }
`;
const DealRow = styled.div`
  display: flex; gap: 12px; overflow-x: auto; padding-bottom: 4px;
  scrollbar-width: thin;
`;
const DealCard = styled.div`
  position: relative;
  min-width: 166px; flex-shrink: 0; background: #fff; border: 1px solid #eef1f2;
  border-radius: 10px; padding: 10px; cursor: pointer;
  box-shadow: 0 1px 3px rgba(20, 40, 45, 0.06);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  &:hover { box-shadow: 0 6px 16px rgba(20, 40, 45, 0.12); transform: translateY(-2px); }
  .incart { position: absolute; top: 8px; left: 8px; z-index: 1; display: inline-flex; align-items: center; gap: 4px;
    background: #037d30; color: #fff; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 6px; }
  .ph { width: 100%; height: 148px; border-radius: 8px; object-fit: contain; background: #fff; display: block; margin-bottom: 8px; padding: 2px; }
  .nm { font-size: 12.5px; font-weight: 600; color: #33454b; line-height: 1.25; height: 32px; overflow: hidden; }
  .pr { font-size: 13px; font-weight: 800; color: #0f9aa8; margin-top: 5px; }
`;
const Main = styled.main`
  min-width: 0;
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
  min-width: 0;
  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 760px) { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  @media (max-width: 360px) { grid-template-columns: 1fr; }
`;
const Skeleton = styled.div`
  border: 1px solid #eef1f2; border-radius: 12px; background: #fff; overflow: hidden;
  .img { width: 100%; padding-top: 80%; background: #eef1f2; }
  .l { height: 12px; margin: 10px 12px; background: #eef1f2; border-radius: 6px; }
  .l.s { width: 50%; }
`;
const Empty = styled.div`
  grid-column: 1 / -1; text-align: center; padding: 50px 0; color: #6b7a80;
  h4 { color: #33454b; margin: 10px 0 4px; }
`;
const Pager = styled.div`
  display: flex; justify-content: center; gap: 6px; margin-top: 28px; flex-wrap: wrap;
  button {
    border: 1px solid #e4e8ea; background: #fff; min-width: 36px; height: 36px;
    border-radius: 9px; cursor: pointer; font-weight: 600; color: #33454b; padding: 0 8px;
  }
  button.active { background: #294b54; color: #fff; border-color: #294b54; }
  button:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SORTS = [
  { v: "relevance", l: "Relevance" },
  { v: "price_asc", l: "Price: Low → High" },
  { v: "price_desc", l: "Price: High → Low" },
  { v: "newest", l: "Newest" },
  { v: "rating", l: "Top rated" },
];

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const DEFAULTS = {
  category: "", priceMin: "", priceMax: "", vendorTypes: [],
  vendorId: "", vendorName: "",
  inStock: false, ratingMin: 0, sort: "relevance", search: "", page: 1,
};

const toParams = (f) => ({
  page: f.page,
  category: f.category || undefined,
  search: f.search || undefined,
  priceMin: f.priceMin || undefined,
  priceMax: f.priceMax || undefined,
  vendorType: f.vendorTypes && f.vendorTypes.length ? f.vendorTypes : undefined,
  vendorId: f.vendorId || undefined,
  inStock: f.inStock ? "true" : undefined,
  ratingMin: f.ratingMin || undefined,
  sort: f.sort,
});

/**
 * "All" means NO category filter here.
 * ────────────────────────────────────────────────────────────────────────────
 * Two different sentinels are in play and they do not agree:
 *   • `categoriesRedux` defaults `selectedCategory` to the STRING "All", and
 *     the backend really does have a catalogue category called "All".
 *   • This page, `CategoryStrip` and `FilterRail` all treat "no filter" as the
 *     EMPTY STRING — `$active={!selected}` is what lights the "All" chip, and
 *     CategoryStrip deliberately drops any backend category named "All" from
 *     its list.
 * Passing "All" straight through would therefore filter the API by a category
 * literally named "All" while lighting up no chip at all. Normalise at the
 * boundary instead.
 */
const NO_CATEGORY = "";
const normaliseCategory = (name) => {
  const value = String(name || "").trim();
  return !value || value.toLowerCase() === "all" ? NO_CATEGORY : value;
};

const ShopCard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { listings, isFetching, fetched, error, numberOfPages, total, facets } =
    useSelector((state) => state.products);
  const categories = useSelector(selectActiveCategories);
  const promoListings = useSelector((state) => state.products.promoListings);
  const cartProducts = useSelector((state) => state.cart.products) || [];
  const inCart = (id) => cartProducts.some((p) => String(p._id) === String(id));
  // A deal card's page, shared by its tap and its links.
  const dealPath = (id) => `/promoProduct/${id}`;
  const openDeal = (id) => history.push(dealPath(id));

  /* Arrive already filtered when the customer picked a category to get here.
     The landing page dispatched `filterCategory(name)` before pushing to
     /shop — but this page never read that slice, so `filters.category` was
     seeded from DEFAULTS ("") on every mount and the choice was silently
     dropped. The URL is the primary source (so a refresh, a bookmark or a
     shared link all keep the filter); Redux is the fallback for any caller
     that still only dispatches. */
  const initialParams = new URLSearchParams(location.search);
  const initialPage = Number(initialParams.get("page")) || 1;
  const reduxCategory = useSelector((state) => state.categories?.selectedCategory);
  const [filters, setFilters] = useState({
    ...DEFAULTS,
    page: initialPage,
    category:
      normaliseCategory(initialParams.get("category")) ||
      normaliseCategory(reduxCategory),
  });
  const [railOpen, setRailOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  /* Leaving the shop clears the hand-off.
     `selectedCategory` is a one-shot baton from the landing page, not durable
     state. Left set, the NEXT arrival at /shop — from the header, the cart,
     anywhere — would silently open pre-filtered to a category the customer
     chose minutes ago and has since forgotten about. Resetting on unmount
     costs nothing while they are here, because the URL carries the filter for
     the lifetime of the visit. */
  useEffect(() => {
    return () => {
      dispatch(filterCategory("All"));
    };
  }, [dispatch]);

  // Categories + deals once.
  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchPromoListings());
  }, [dispatch]);

  // Live catalogue visibility (catalog:public room):
  //  • product:statusChanged — a vendor (or staff) deactivated/reactivated an
  //    INDIVIDUAL item. Deactivation drops it from the grid + page caches
  //    instantly; reactivation refetches the current page (server-authoritative).
  //  • brand:statusChanged — a whole brand was delisted/relisted.
  // The socket is a freshness layer only — backend readers still filter by
  // visibility, so a missed event can never expose a hidden product.
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useCatalogUpdates({
    "product:statusChanged": (payload) => {
      if (!payload.productId) return;
      if (payload.enabled === false) {
        dispatch(removeCatalogItem(payload.productId));
      } else {
        dispatch(fetchCatalog(toParams(filtersRef.current)));
      }
    },
    "brand:statusChanged": (payload) => {
      if (!payload.brandId) return;
      if (payload.isActive === false) {
        dispatch(removeCatalogVendorItems(payload.brandId));
      } else {
        dispatch(fetchCatalog(toParams(filtersRef.current)));
      }
    },
  });

  // Fetch catalogue whenever filters change; keep ?page in the URL.
  useEffect(() => {
    dispatch(fetchCatalog(toParams(filters)));
    /* Keep the category in the URL. This used to rebuild the query string as
       exactly `?page=N`, which stripped every other param — so even a correct
       ?category= would have been erased on this effect's first run. */
    const search = new URLSearchParams();
    search.set("page", String(filters.page));
    if (filters.category) search.set("category", filters.category);
    const desired = `?${search.toString()}`;
    if (location.pathname === "/shop" && location.search !== desired) {
      history.replace({ pathname: "/shop", search: desired });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    // eslint-disable-next-line
  }, [filters]);

  // Debounced search → filter patch.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput, page: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const patch = useCallback((p) => {
    setFilters((f) => ({ ...f, ...p, page: p.page != null ? p.page : 1 }));
  }, []);

  const clearAll = () => { setSearchInput(""); setFilters({ ...DEFAULTS }); };

  // Active filter chips
  const chips = [];
  if (filters.category) chips.push({ k: "Category", v: filters.category, clear: () => patch({ category: "" }) });
  if (filters.vendorId) chips.push({ k: "Vendor", v: filters.vendorName || "Vendor", clear: () => patch({ vendorId: "", vendorName: "" }) });
  (filters.vendorTypes || []).forEach((vt) =>
    chips.push({ k: "Type", v: vt, clear: () => patch({ vendorTypes: filters.vendorTypes.filter((x) => x !== vt) }) })
  );
  if (filters.priceMin || filters.priceMax)
    chips.push({ k: "Price", v: `${filters.priceMin || 0}–${filters.priceMax || "∞"}`, clear: () => patch({ priceMin: "", priceMax: "" }) });
  if (filters.inStock) chips.push({ k: "", v: "In stock", clear: () => patch({ inStock: false }) });
  if (filters.ratingMin) chips.push({ k: "Rating", v: `${filters.ratingMin}★ & up`, clear: () => patch({ ratingMin: 0 }) });
  if (filters.search) chips.push({ k: "Search", v: filters.search, clear: () => { setSearchInput(""); patch({ search: "" }); } });

  const deals = (promoListings || []).slice(0, 12);

  const renderGrid = () => {
    if (isFetching) {
      return Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i}><div className="img" /><div className="l" /><div className="l s" /></Skeleton>
      ));
    }
    if (error) {
      return (
        <Empty>
          <h4>Couldn’t load products</h4>
          <p>Something went wrong. Please try again.</p>
          <button onClick={() => dispatch(fetchCatalog(toParams(filters)))}
            style={{ marginTop: 10, border: "1px solid #03660c", background: "#03660c", color: "#fff", borderRadius: 9, padding: "8px 16px", cursor: "pointer", fontWeight: 700 }}>
            Retry
          </button>
        </Empty>
      );
    }
    if (fetched && (!listings || listings.length === 0)) {
      return (
        <Empty>
          <h4>No products match these filters</h4>
          <p>Try clearing a filter or widening your price range.</p>
          {chips.length ? (
            <button onClick={clearAll}
              style={{ marginTop: 10, border: "1px solid #03660c", background: "#fff", color: "#03660c", borderRadius: 9, padding: "8px 16px", cursor: "pointer", fontWeight: 700 }}>
              Clear all filters
            </button>
          ) : null}
        </Empty>
      );
    }
    return (listings || []).map((p) => <ProductCard key={p._id} product={p} />);
  };

  // Pagination buttons (windowed)
  const pages = [];
  const np = Number(numberOfPages) || 0;
  if (np > 1) {
    const cur = filters.page;
    const start = Math.max(1, cur - 2);
    const end = Math.min(np, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
  }

  return (
    <Layout>
      <Page>
        <StripWrap>
          <CategoryStrip
            categories={categories || []}
            selected={filters.category}
            onSelect={(cat) => patch({ category: cat })}
          />
        </StripWrap>

        <Shell>
          <RailCol $open={railOpen}>
            <FilterRail categories={categories || []} facets={facets} value={filters} onChange={patch} />
          </RailCol>

          <Main>
            <Toolbar>
              <Count><b>{fmt(total)}</b> products{filters.category ? ` in ${filters.category}` : ""}</Count>
              <SearchBox>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7a80" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  placeholder="Search products…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </SearchBox>
              <FiltersBtn onClick={() => setRailOpen((o) => !o)}>
                {railOpen ? "Hide filters" : "Filters"}
              </FiltersBtn>
              <SortWrap>
                <label htmlFor="sortSel">Sort</label>
                <select id="sortSel" value={filters.sort} onChange={(e) => patch({ sort: e.target.value })}>
                  {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </SortWrap>
            </Toolbar>

            {chips.length > 0 && (
              <Chips>
                {chips.map((c, i) => (
                  <Chip key={i}>{c.k ? <b>{c.k}:</b> : null} {c.v}<span className="x" onClick={c.clear}>✕</span></Chip>
                ))}
                {chips.length > 1 && <ClearAll onClick={clearAll}>Clear all ✕</ClearAll>}
              </Chips>
            )}

            {deals.length > 0 && !filters.search && (
              <DealsBox>
                <h3>Today’s Deals</h3>
                <DealRow>
                  {deals.map((d) => (
                    <DealCard key={d._id} onClick={() => openDeal(d._id)}>
                      {inCart(d._id) && <span className="incart">✓ In cart</span>}
                      <CrawlLink
                        to={dealPath(d._id)}
                        onNavigate={() => openDeal(d._id)}
                        style={{ display: "block" }}
                      >
                        {d.product_image ? (
                          <img className="ph" src={optimizedImage(d.product_image, "card")} loading="lazy" alt={d.title} />
                        ) : <div className="ph" />}
                      </CrawlLink>
                      <div className="nm">
                        <CrawlLink to={dealPath(d._id)} onNavigate={() => openDeal(d._id)}>
                          {d.title}
                        </CrawlLink>
                      </div>
                      <div className="pr">{formatCurrency(d.price)}</div>
                    </DealCard>
                  ))}
                </DealRow>
              </DealsBox>
            )}

            <Grid>{renderGrid()}</Grid>

            {np > 1 && !isFetching && (
              <Pager>
                <button disabled={filters.page <= 1} onClick={() => patch({ page: filters.page - 1 })}>‹</button>
                {pages[0] > 1 && <button onClick={() => patch({ page: 1 })}>1</button>}
                {pages[0] > 2 && <span style={{ alignSelf: "center", color: "#9aa7ac" }}>…</span>}
                {pages.map((i) => (
                  <button key={i} className={i === filters.page ? "active" : ""} onClick={() => patch({ page: i })}>{i}</button>
                ))}
                {pages[pages.length - 1] < np - 1 && <span style={{ alignSelf: "center", color: "#9aa7ac" }}>…</span>}
                {pages[pages.length - 1] < np && <button onClick={() => patch({ page: np })}>{np}</button>}
                <button disabled={filters.page >= np} onClick={() => patch({ page: filters.page + 1 })}>›</button>
              </Pager>
            )}

            {isFetching && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
                <BounceLoader size={36} color={"#03660C"} />
              </div>
            )}
          </Main>
        </Shell>
      </Page>
    </Layout>
  );
};

export default ShopCard;
